import { useEffect, useRef, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { messageOf, unwrap } from '@truecare/api-client';
import { Alert, Button } from '@truecare/ui';
import { api } from '../api';
import { Avatar } from './Avatar';

/**
 * Phase 17 — caregiver profile photo control.
 *
 * Flow (mirrors the credential / void-cheque presigned-upload pattern):
 *   1. On file pick, resize client-side to a 512×512 centre-cropped JPEG
 *      (drawn onto a canvas → toBlob('image/jpeg', 0.85)).
 *   2. POST /me/caregiver/profile-photo/upload-url with the blob's
 *      contentType + contentLength → { uploadUrl, key }.
 *   3. PUT the blob directly to object storage.
 *   4. PUT /me/caregiver/profile-photo { key } to confirm + record.
 *   5. Invalidate the ['caregiver','profile-photo'] query.
 *
 * `null` photo → the Avatar renders the default initials avatar.
 */
const RESIZE_DIMENSION = 512;
const JPEG_QUALITY = 0.85;

export function CaregiverProfilePhoto({ name }: { name: string }) {
  const qc = useQueryClient();
  const fileInputRef = useRef<HTMLInputElement>(null);
  const [preview, setPreview] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);

  // `URL.createObjectURL` leaks until the URL is revoked. Track the live preview
  // URL in a ref so every replacement (a new pick, or clearing on upload
  // success/failure) revokes the previous one, and so does unmount. Going
  // through this setter is the only way `preview` should change.
  const previewUrlRef = useRef<string | null>(null);
  const replacePreview = (next: string | null) => {
    if (previewUrlRef.current) URL.revokeObjectURL(previewUrlRef.current);
    previewUrlRef.current = next;
    setPreview(next);
  };
  useEffect(
    () => () => {
      if (previewUrlRef.current) URL.revokeObjectURL(previewUrlRef.current);
    },
    [],
  );

  const photoQuery = useQuery({
    queryKey: ['caregiver', 'profile-photo'],
    queryFn: () => unwrap(api.GET('/api/v1/me/caregiver/profile-photo')),
    retry: false,
  });

  const uploadMutation = useMutation({
    mutationFn: async (file: File) => {
      const blob = await resizeToSquareJpeg(file);
      // Step 1: signed URL
      const urlResp = await unwrap(
        api.POST('/api/v1/me/caregiver/profile-photo/upload-url', {
          body: { contentType: 'image/jpeg', contentLength: blob.size },
        }),
      );
      // Step 2: PUT resized blob to object storage
      const putResp = await fetch(urlResp.uploadUrl, {
        method: 'PUT',
        headers: { 'Content-Type': 'image/jpeg' },
        body: blob,
      });
      if (!putResp.ok) {
        throw new Error(
          `Could not upload to storage (HTTP ${String(putResp.status)}). Try again.`,
        );
      }
      // Step 3: confirm + record
      return unwrap(
        api.PUT('/api/v1/me/caregiver/profile-photo', { body: { key: urlResp.key } }),
      );
    },
    onSuccess: () => {
      replacePreview(null);
      setError(null);
      void qc.invalidateQueries({ queryKey: ['caregiver', 'profile-photo'] });
    },
    onError: (e) => {
      replacePreview(null);
      setError(messageOf(e, 'Upload failed. Try again.'));
    },
  });

  const removeMutation = useMutation({
    mutationFn: () => unwrap(api.DELETE('/api/v1/me/caregiver/profile-photo')),
    onSuccess: () => {
      setError(null);
      void qc.invalidateQueries({ queryKey: ['caregiver', 'profile-photo'] });
    },
    onError: (e) => {
      setError(messageOf(e, 'Could not remove photo. Try again.'));
    },
  });

  const currentUrl = photoQuery.data?.url ?? null;
  const hasPhoto = currentUrl !== null;
  const busy = uploadMutation.isPending || removeMutation.isPending;

  function onPick(e: React.ChangeEvent<HTMLInputElement>) {
    const file = e.target.files?.[0];
    // Reset the input so re-picking the same file fires change again.
    e.target.value = '';
    if (!file) return;
    if (!file.type.startsWith('image/')) {
      setError('Please choose an image file (JPEG, PNG, or WebP).');
      return;
    }
    setError(null);
    replacePreview(URL.createObjectURL(file));
    uploadMutation.mutate(file);
  }

  return (
    <div className="flex items-center gap-4">
      <div className="relative">
        <Avatar photoUrl={preview ?? currentUrl} name={name} size={72} />
        {busy && (
          <div className="absolute inset-0 flex items-center justify-center rounded-full bg-paper/60">
            <span
              className="h-5 w-5 animate-spin rounded-full border-2 border-forest border-t-transparent"
              aria-label="Uploading"
            />
          </div>
        )}
      </div>

      <div className="flex flex-col gap-2">
        <input
          ref={fileInputRef}
          type="file"
          accept="image/jpeg,image/png,image/webp"
          className="hidden"
          onChange={onPick}
          data-testid="caregiver-photo-input"
        />
        <div className="flex flex-wrap gap-2">
          <Button
            type="button"
            variant="secondary"
            size="sm"
            disabled={busy}
            onClick={() => fileInputRef.current?.click()}
            data-testid="caregiver-photo-upload"
          >
            {hasPhoto ? 'Change photo' : 'Upload photo'}
          </Button>
          {hasPhoto && (
            <Button
              type="button"
              variant="ghost"
              size="sm"
              disabled={busy}
              onClick={() => removeMutation.mutate()}
              data-testid="caregiver-photo-remove"
            >
              Remove
            </Button>
          )}
        </div>
        <p className="font-body text-xs text-ink-muted">
          JPEG, PNG, or WebP. We resize it to a square automatically.
        </p>
        {error && (
          <Alert tone="danger" data-testid="caregiver-photo-error">
            {error}
          </Alert>
        )}
      </div>
    </div>
  );
}

/**
 * Load a File into an Image, centre-crop the largest square, draw it onto a
 * 512×512 canvas, and export as a JPEG blob. Runs entirely client-side so we
 * upload a small, uniform image regardless of the source dimensions.
 */
async function resizeToSquareJpeg(file: File): Promise<Blob> {
  const objectUrl = URL.createObjectURL(file);
  try {
    const img = await loadImage(objectUrl);
    const side = Math.min(img.naturalWidth, img.naturalHeight);
    const sx = (img.naturalWidth - side) / 2;
    const sy = (img.naturalHeight - side) / 2;

    const canvas = document.createElement('canvas');
    canvas.width = RESIZE_DIMENSION;
    canvas.height = RESIZE_DIMENSION;
    const ctx = canvas.getContext('2d');
    if (!ctx) throw new Error('Could not process the image (no canvas context).');
    ctx.drawImage(img, sx, sy, side, side, 0, 0, RESIZE_DIMENSION, RESIZE_DIMENSION);

    return await new Promise<Blob>((resolve, reject) => {
      canvas.toBlob(
        (blob) => (blob ? resolve(blob) : reject(new Error('Could not encode the image.'))),
        'image/jpeg',
        JPEG_QUALITY,
      );
    });
  } finally {
    URL.revokeObjectURL(objectUrl);
  }
}

function loadImage(src: string): Promise<HTMLImageElement> {
  return new Promise((resolve, reject) => {
    const img = new Image();
    img.onload = () => resolve(img);
    img.onerror = () => reject(new Error('Could not read the image file.'));
    img.src = src;
  });
}
